// One ranked hedge strategy. Click the header to expand; the body shows the
// score breakdown, leg table, payoff chart + tabular grid, summary stats, tax
// note, and notes list. Extracted from HedgeLabTable.tsx so the table
// orchestrator stays under the god-file threshold.

import type { HedgeLeg, HedgeStrategy, StrategyType } from '@/lib/hedge-structures';
import { PayoffChart } from '../PayoffChart';

const TYPE_LABEL: Record<StrategyType, string> = {
  trim: 'Trim',
  covered_call: 'Covered Call',
  protective_put: 'Protective Put',
  collar: 'Collar',
  bear_put_spread: 'Bear Put Spread',
  stock_replacement: 'Stock Replacement',
  cash_secured_put: 'Cash-Secured Put',
  hold: 'Hold',
};

const TYPE_BORDER: Record<StrategyType, string> = {
  trim: 'border-zinc-500/40',
  covered_call: 'border-emerald-500/40',
  protective_put: 'border-blue-500/40',
  collar: 'border-amber-500/40',
  bear_put_spread: 'border-cyan-500/40',
  stock_replacement: 'border-violet-500/40',
  cash_secured_put: 'border-green-500/40',
  hold: 'border-slate-500/40',
};

const TYPE_BADGE: Record<StrategyType, string> = {
  trim: 'bg-zinc-500/15 text-zinc-300',
  covered_call: 'bg-emerald-500/15 text-emerald-300',
  protective_put: 'bg-blue-500/15 text-blue-300',
  collar: 'bg-amber-500/15 text-amber-300',
  bear_put_spread: 'bg-cyan-500/15 text-cyan-300',
  stock_replacement: 'bg-violet-500/15 text-violet-300',
  cash_secured_put: 'bg-green-500/15 text-green-300',
  hold: 'bg-slate-500/15 text-slate-300',
};

function tierBadge(tier: 'BEST FIT' | 'VIABLE' | 'NOT RECOMMENDED' | undefined): { text: string; cls: string } {
  if (tier == null) return { text: '', cls: '' };
  if (tier === 'BEST FIT') return { text: 'BEST FIT', cls: 'bg-emerald-500/25 text-emerald-200 border border-emerald-500/50' };
  if (tier === 'VIABLE') return { text: 'VIABLE', cls: 'bg-zinc-500/20 text-zinc-300 border border-zinc-500/30' };
  return { text: 'NOT RECOMMENDED', cls: 'bg-red-500/15 text-red-300 border border-red-500/30' };
}

function ivRankChip(ivRank: number | null | undefined): { text: string; cls: string } | null {
  if (ivRank == null) return null;
  if (ivRank >= 70) return { text: `IV rank ${ivRank.toFixed(0)} (rich)`, cls: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' };
  if (ivRank >= 50) return { text: `IV rank ${ivRank.toFixed(0)} (elevated)`, cls: 'bg-amber-500/15 text-amber-300 border border-amber-500/30' };
  if (ivRank >= 30) return { text: `IV rank ${ivRank.toFixed(0)} (normal)`, cls: 'bg-slate-500/15 text-slate-300 border border-slate-500/30' };
  return { text: `IV rank ${ivRank.toFixed(0)} (cheap)`, cls: 'bg-red-500/15 text-red-300 border border-red-500/30' };
}

function scoreChipClass(score: number | undefined): string {
  if (score == null) return 'bg-[#1e293b]/40 text-[#94a3b8] border-[#334155]';
  if (score >= 80) return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50';
  if (score >= 60) return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30';
  if (score >= 40) return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return 'bg-red-500/10 text-red-300 border-red-500/30';
}

function LegRow({ leg }: { leg: HedgeLeg }) {
  const actionTone = leg.action === 'sell' ? 'text-emerald-300' : 'text-amber-300';
  const strikeText = leg.asset === 'stock' ? '—' : `$${leg.strike} ${leg.expiry ?? ''}`;
  return (
    <tr>
      <td className={`pr-2 py-0.5 font-semibold uppercase ${actionTone}`}>{leg.action}</td>
      <td className="pr-2 py-0.5">{leg.asset}</td>
      <td className="pr-2 py-0.5">{strikeText}</td>
      <td className="text-right pr-2 py-0.5">${leg.pricePerUnit.toFixed(2)}</td>
      <td className="text-right pr-2 py-0.5 text-[#94a3b8]">
        {leg.bid != null && leg.ask != null ? `${leg.bid.toFixed(2)}/${leg.ask.toFixed(2)}` : '—'}
      </td>
      <td className="text-right pr-2 py-0.5 text-[#94a3b8]">{leg.iv != null ? `${leg.iv.toFixed(0)}%` : '—'}</td>
      <td className="text-right py-0.5 text-[#94a3b8]">{leg.delta != null ? leg.delta.toFixed(2) : '—'}</td>
    </tr>
  );
}

function SummaryStat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: 'good' | 'bad' | 'caution' | 'neutral' }) {
  const valColor = tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-red-300' : tone === 'caution' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-[#0f1623] border border-[#1e293b] rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-[#64748b] mb-0.5">{label}</div>
      <div className={`text-[12px] font-semibold tabular-nums ${valColor}`}>{value}</div>
      <div className="text-[9px] text-[#64748b] mt-0.5">{sub}</div>
    </div>
  );
}

export function StrategyCard({ strategy: s, expanded, onToggle }: { strategy: HedgeStrategy; expanded: boolean; onToggle: () => void }) {
  const isCredit = s.netUpfrontPerShare < 0;
  const isHold = s.type === 'hold';
  const tb = tierBadge(s.fitTier);
  const iv = ivRankChip(s.ivRank);
  const scoreCls = scoreChipClass(s.fitScore);
  const isBestFit = s.fitTier === 'BEST FIT';
  // Every strategy header makes BUY/SELL explicit per leg so the trade reads
  // off the card without expanding it. Stock legs (Trim) are dropped — option
  // legs only.
  const optionLegs = s.legs.filter((l) => l.asset === 'call' || l.asset === 'put');
  return (
    <div className={`bg-[#0a0e14] border ${isBestFit ? 'border-emerald-500/60' : TYPE_BORDER[s.type]} rounded-md overflow-hidden ${isBestFit ? 'shadow-[0_0_24px_rgba(16,185,129,0.18)]' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline justify-between gap-3 px-3 py-2 hover:bg-[#1e293b]/30 transition-colors text-left"
      >
        <div className="flex items-baseline gap-2 flex-1 min-w-0">
          {tb.text && <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${tb.cls}`}>{tb.text}</span>}
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${TYPE_BADGE[s.type]} flex-shrink-0`}>
            {TYPE_LABEL[s.type]}
          </span>
          {iv && (s.type === 'covered_call' || s.type === 'collar' || s.type === 'cash_secured_put') && (
            <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${iv.cls}`}>{iv.text}</span>
          )}
          {/* Explicit BUY / SELL pills per option leg — green=sell (collect),
              red=buy (pay). */}
          {optionLegs.map((leg, i) => {
            const cls = leg.action === 'sell'
              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
              : 'bg-red-500/15 text-red-300 border-red-500/40';
            const sideLetter = leg.asset === 'call' ? 'C' : 'P';
            return (
              <span
                key={i}
                className={`text-[10px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border flex-shrink-0 ${cls}`}
                title={`${leg.action.toUpperCase()} ${leg.expiry ?? ''} $${leg.strike} ${leg.asset}`}
              >
                {leg.action} ${leg.strike}{sideLetter}
              </span>
            );
          })}
          <span className="text-[12px] text-[#cbd5e1] truncate">{s.description}</span>
        </div>
        <div className="flex items-baseline gap-2 flex-shrink-0 text-[11px] tabular-nums">
          {s.fitScore != null && (
            <span
              className={`text-[11px] font-bold tabular-nums px-2 py-0.5 rounded border ${scoreCls}`}
              title="Click the row to see the full score breakdown"
            >
              SCORE {s.fitScore}
            </span>
          )}
          {!isHold && (
            <span className={isCredit ? 'text-emerald-300 font-semibold' : 'text-amber-300 font-semibold'}>
              {isCredit ? 'credit' : 'debit'} ${Math.abs(s.netUpfrontPerShare).toFixed(2)}/sh
            </span>
          )}
          {isHold && <span className="text-[#94a3b8] font-semibold">no action</span>}
          <span className="text-[#475569]">{expanded ? '▾' : '▸'}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#1e293b] px-3 py-3 space-y-3">
          {s.fitReason && (
            <div className={`text-[11px] leading-relaxed px-3 py-2 rounded border ${
              s.fitTier === 'BEST FIT' ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-200' :
              s.fitTier === 'NOT RECOMMENDED' ? 'border-red-500/40 bg-red-500/5 text-red-200' :
              'border-[#1e293b] bg-[#0f1623] text-[#cbd5e1]'
            }`}>
              <span className="text-[9px] uppercase tracking-wider font-semibold mr-2 opacity-70">Tier · {s.fitTier}</span>
              {s.fitReason}
            </div>
          )}
          {s.fitFactors && s.fitFactors.length > 0 && (
            <div className="border border-[#1e293b] rounded-md p-3 bg-[#0f1623]">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">
                  Score breakdown
                </span>
                <span className="text-[10px] text-[#64748b]">
                  base 50 {s.fitFactors.reduce((acc, f) => acc + f.delta, 0) >= 0 ? '+' : ''}{s.fitFactors.reduce((acc, f) => acc + f.delta, 0).toFixed(0)} = <span className="text-white font-semibold">{s.fitScore}/100</span>
                </span>
              </div>
              <div className="space-y-1.5">
                {s.fitFactors.map((f, i) => (
                  <div key={i} className="flex items-baseline gap-2 leading-tight text-[11px]">
                    <span className={`font-mono text-[10px] tabular-nums w-8 text-right flex-shrink-0 ${f.delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {f.delta >= 0 ? '+' : ''}{f.delta.toFixed(0)}
                    </span>
                    <span className={`flex-shrink-0 w-[110px] ${f.delta >= 0 ? 'text-emerald-300/80' : 'text-red-300/80'}`}>{f.factor}</span>
                    <span className="text-[#94a3b8] text-[10px] leading-snug">{f.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[12px] text-[#cbd5e1] leading-relaxed">{s.rationale}</p>

          {s.legs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">Trade legs</div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[#64748b]">
                    <th className="text-left font-semibold pb-1 pr-2">Action</th>
                    <th className="text-left font-semibold pb-1 pr-2">Asset</th>
                    <th className="text-left font-semibold pb-1 pr-2">Strike / Expiry</th>
                    <th className="text-right font-semibold pb-1 pr-2">$/unit</th>
                    <th className="text-right font-semibold pb-1 pr-2">Bid/Ask</th>
                    <th className="text-right font-semibold pb-1 pr-2">IV</th>
                    <th className="text-right font-semibold pb-1">Δ</th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums text-[#cbd5e1]">
                  {s.legs.map((leg, i) => <LegRow key={i} leg={leg} />)}
                </tbody>
              </table>
            </div>
          )}

          {s.scenarios && s.scenarios.length > 0 && (
            <div className="space-y-3">
              {/* Payoff chart shows where you profit, where you lose, capped
                  upside / downside, etc. The 7-cell tabular grid below is a
                  precise per-share readout. */}
              <PayoffChart strategy={s} />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">P&amp;L at expiry — tabular (per share)</div>
                <div className="grid grid-cols-7 gap-1 text-[10px] tabular-nums font-mono text-center">
                  {s.scenarios.map((sc, i) => {
                    const tone = sc.pnlPerShare > 0 ? 'text-emerald-300 bg-emerald-500/10' : sc.pnlPerShare < 0 ? 'text-red-300 bg-red-500/10' : 'text-[#cbd5e1] bg-[#1e293b]/50';
                    return (
                      <div key={i} className={`rounded px-1.5 py-1 ${tone}`}>
                        <div className="text-[#64748b] text-[9px]">${sc.priceAtExpiry.toFixed(0)}</div>
                        <div className="font-semibold">{sc.pnlPerShare >= 0 ? '+' : ''}${sc.pnlPerShare.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <SummaryStat
              label={isCredit ? 'You collect' : 'You pay'}
              value={`$${Math.abs(s.netUpfrontPerShare).toFixed(2)}/sh`}
              sub={`${(Math.abs(s.netUpfrontPctOfSpot) * 100).toFixed(2)}% of spot · cash ${isCredit ? 'in' : 'out'} on entry`}
              tone={isCredit ? 'good' : 'caution'}
            />
            {s.taxImpactPerShare != null && s.taxImpactPerShare !== 0 && (
              <SummaryStat
                label="Tax impact"
                value={`${s.taxImpactPerShare >= 0 ? '+' : '−'}$${Math.abs(s.taxImpactPerShare).toFixed(2)}/sh`}
                sub={s.taxImpactPerShare < 0 ? 'tax bill on realization' : 'tax shelter from realized loss'}
                tone={s.taxImpactPerShare < 0 ? 'bad' : 'good'}
              />
            )}
            {s.taxImpactPerShare != null && s.taxImpactPerShare !== 0 && (
              <SummaryStat
                label="After-tax net"
                value={`${s.netUpfrontPerShare + s.taxImpactPerShare < 0 ? '+' : '−'}$${Math.abs(s.netUpfrontPerShare + s.taxImpactPerShare).toFixed(2)}/sh`}
                sub="cash + tax bill, all in"
                tone={s.netUpfrontPerShare + s.taxImpactPerShare < 0 ? 'good' : 'caution'}
              />
            )}
            {s.maxGainPerShare != null && (
              <SummaryStat label="Max gain" value={`+$${s.maxGainPerShare.toFixed(2)}`} sub={`${(s.maxGainPerShare / s.spot * 100).toFixed(1)}%`} tone="good" />
            )}
            {s.maxLossPerShare != null && (
              <SummaryStat label="Max loss" value={`−$${s.maxLossPerShare.toFixed(2)}`} sub={`${(s.maxLossPerShare / s.spot * 100).toFixed(1)}%`} tone="bad" />
            )}
            {s.breakevenPerShare != null && (
              <SummaryStat label="Breakeven" value={`$${s.breakevenPerShare.toFixed(2)}`} sub={`${((s.breakevenPerShare - s.spot) / s.spot * 100).toFixed(1)}% from spot`} tone="neutral" />
            )}
            {s.premiumYieldAnnualized != null && (
              <SummaryStat label="Yield (ann.)" value={`${(s.premiumYieldAnnualized * 100).toFixed(0)}%`} sub="if rolled" tone="good" />
            )}
            {s.ivAvg != null && s.ivAvg > 0 && (
              <SummaryStat label="IV avg" value={`${s.ivAvg.toFixed(0)}%`} sub={s.ivAvg > 60 ? 'elevated' : s.ivAvg > 35 ? 'moderate' : 'low'} tone="neutral" />
            )}
          </div>

          {s.taxNote && (
            <div className="border-t border-[#1e293b]/60 pt-2">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Tax treatment (assumed 25% LT cap gains, 37% short-term ordinary)</div>
              <p className="text-[11px] text-[#cbd5e1] leading-relaxed">{s.taxNote}</p>
            </div>
          )}

          {s.notes && s.notes.length > 0 && (
            <div className="border-t border-[#1e293b]/60 pt-2">
              <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1">Notes</div>
              <ul className="text-[11px] text-[#cbd5e1] leading-relaxed space-y-1 list-disc list-inside">
                {s.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
